import { isLiveSession } from "@constants/demo/common";
import { isProethicaTestId } from "@constants/demo/proethica-tests";
import { useSession } from "@features/session";
import { useProethicaSessionStore } from "@features/session/hooks/useProethicaSessionStore";
import type { MixedItemTag } from "@memorang/types";
import type { TFunction } from "i18next";
import { useCallback, useState } from "react";
import {
	getProethicaModuleListProps,
	getProethicaModulesGroupedByStatus,
	resolveProethicaModuleItems,
} from "../helpers/proethica-module-list";

// Single source of truth for ProEthica module buttons across every surface
// (Modules screen, home list, quick-study dialog): the persisted tracker drives
// Start/Resume/View Report, and `startingModuleId` drives the per-button loader
// while a once-only session is created or resumed.
export const useProethicaModuleList = () => {
	const sessionByModule = useProethicaSessionStore(
		(state) => state.sessionByModule,
	);
	const { handleDirectSessionStart, handleStartProethicaTest } = useSession();
	const [startingModuleId, setStartingModuleId] = useState<string | null>(null);

	const liveStatusFor = useCallback(
		(tag: MixedItemTag) => sessionByModule[String(tag.id)]?.status,
		[sessionByModule],
	);

	const getModuleListItemProps = useCallback(
		(tag: MixedItemTag, t: TFunction) => ({
			...getProethicaModuleListProps(tag, t, liveStatusFor(tag)),
			loading: startingModuleId === String(tag.id),
		}),
		[liveStatusFor, startingModuleId],
	);

	const getModulesGroupedByStatus = useCallback(
		(tags: MixedItemTag[], t: TFunction) =>
			getProethicaModulesGroupedByStatus(tags, t, liveStatusFor),
		[liveStatusFor],
	);

	const resolveModuleItems = useCallback(
		(items: MixedItemTag[]) =>
			resolveProethicaModuleItems(items, isLiveSession),
		[],
	);

	const startModule = useCallback(
		async (moduleId: string) => {
			setStartingModuleId(moduleId);
			try {
				if (isProethicaTestId(moduleId)) {
					await handleStartProethicaTest({ testId: moduleId });
				} else {
					await handleDirectSessionStart({ push: true, moduleId });
				}
			} finally {
				setStartingModuleId(null);
			}
		},
		[handleDirectSessionStart, handleStartProethicaTest],
	);

	return {
		sessionByModule,
		startingModuleId,
		liveStatusFor,
		getModuleListItemProps,
		getModulesGroupedByStatus,
		resolveModuleItems,
		startModule,
	};
};
